import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { useApi } from "../../apiHelpers";
import { DataTable } from "../../components/table/DataTable";
import BackLink from "../../components/BackLink";
import PageLayout from '../../components/ui/PageLayout';
import { buttonClass } from '../../components/ui/Button';
import { SegTrack } from '../../components/ui/SegTrack';
import { Tag } from '../../components/ui/Tag';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { CATEGORY, alpha } from '../../components/ui/categoryColors';

// Status colour helper: a hue from the shared OKLCH family, or undefined for secondary text.
const STATUS_HUE = {
  pending_review: 'yellow',
  approved: 'blue',
  rejected: 'red',
  received: 'indigo',
  in_repair: 'orange',
  repaired: 'cyan',
  quality_check: 'cyan',
  completed: 'green',
  unrepairable: 'red',
  shipped: 'teal',
};
const inkOf = (hue) => ({ color: CATEGORY[hue].ink.oklch });
const getStatusStyle = (status) => (STATUS_HUE[status] ? inkOf(STATUS_HUE[status]) : undefined);

// Per-row tint by entry type, so it rides `rowProps` rather than a column
// class (a column class would tint every row). Rest tone only: the vessel owns
// hover, and restating a hover here would be two `hover:bg-*` utilities on one
// element resolving by stylesheet order. An RMA row is the only clickable one,
// which is why it is the only one carrying cursor-pointer.
const ROW_HUE = { flag: 'yellow', report: 'purple', install: 'teal' };
const rowTint = (item) => {
  if (item.type === 'rma') return item.isActive ? 'bg-primary/10 cursor-pointer' : 'cursor-pointer';
  return '';
};
// Colour rides a style: the hue is data, so it cannot be a static class.
const rowStyle = (item) => {
  const hue = item.type === 'rma' ? (item.isActive ? null : 'green') : ROW_HUE[item.type];
  if (!hue) return undefined;
  const style = { backgroundColor: alpha(CATEGORY[hue].mark, item.type === 'rma' ? 0.08 : 0.12) };
  return item.type === 'rma' ? style : { ...style, color: CATEGORY[hue].ink.oklch };
};

// ---------------------------------------------------------------------------
// Columns for the shared DataTable.
//
// Module scope per the DataTable contract: the array never changes identity,
// so the 10s poll below re-renders cells instead of remounting them. Nothing
// here is volatile, so the table needs no `meta`.
//
// Display-only columns (`{id, cell}`) and no `sortable`, deliberately. This
// page's order is COMPOUND — active RMAs first, then date descending — and it
// is built in the listItems memo below. A sortable date header would re-sort by
// date alone on the first click and take the open-RMA hoist with it, and there
// is no way to say "sort by this column, but keep that group on top" through a
// column sort. So the pre-sorted array IS the order, and it is the only one.
//
// The row click is a property of the ROW (RMA rows only), so it lives in
// `rowProps` on the page below.
//
// Plain JavaScript file: object literals, and <DataTable> with no type
// argument. The generic exists for the .tsx consumers and costs this file
// nothing.
// ---------------------------------------------------------------------------

const HISTORY_COLUMNS = [
  {
    id: "type",
    header: "Type",
    meta: { headerClass: "w-[80px]" },
    cell: ({ row }) => {
      const type = row.original.type;
      if (type === 'maint') return <span className="text-xs font-semibold" style={inkOf('blue')}>MAINT</span>;
      if (type === 'flag') return <span className="text-xs font-semibold" style={inkOf('orange')}>FLAG</span>;
      if (type === 'report') return <span className="text-xs font-semibold" style={inkOf('purple')}>RPRT</span>;
      if (type === 'rma') return (
        <Tag tone="accent" size="sm" className="font-bold">RMA</Tag>
      );
      if (type === 'install') return <span className="text-xs font-semibold" style={inkOf('teal')}>BUILD</span>;
      return null;
    },
  },
  {
    id: "date",
    header: "Date",
    meta: { headerClass: "w-[150px]" },
    cell: ({ row }) => <span className="font-mono text-on-surface">{row.original.date}</span>,
  },
  {
    id: "user",
    header: "User",
    meta: { headerClass: "w-[120px]" },
    cell: ({ row }) => <span className="text-on-surface-variant">{row.original.user}</span>,
  },
  {
    id: "desc",
    header: "Description",
    cell: ({ row }) => {
      const item = row.original;
      const value = item.desc;

      // RMA: Show category + status
      if (item.type === 'rma') {
        return (
          <div className="flex items-center gap-3">
            <span className="text-white">{item.category}</span>
            <span className="text-xs text-on-surface-variant" style={getStatusStyle(item.status)}>
              {item.itemStatus === 'rejected' ? 'Rejected' :
               item.itemStatus === 'pending_review' ? 'Pending Review' :
               (item.status || 'pending').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
        );
      }

      // URL: Show as link
      if (typeof value === "string" && value.startsWith("http")) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            LOG PULLED
          </a>
        );
      }

      return <span className="text-white">{value}</span>;
    },
  },
  {
    id: "action",
    header: "",
    meta: { align: "right", headerClass: "w-[70px]" },
    cell: ({ row }) => {
      const item = row.original;
      // stopPropagation on both: an RMA row's own click navigates, and a link
      // inside it must win rather than fire twice.
      if (item.type === 'rma') {
        return (
          <Link
            to={`/rma/${item.rma_number}`}
            className={buttonClass('quiet', 'sm')}
            onClick={(e) => e.stopPropagation()}
          >
            View →
          </Link>
        );
      }
      return (
        <Link
          to={`/drone/${item.drone_serial}/${item.type}/${item.rawId}`}
          className={buttonClass('quiet', 'sm')}
          onClick={(e) => e.stopPropagation()}
        >
          Edit
        </Link>
      );
    },
  },
];

const VehicleHistory = () => {
  const { serialnumber } = useParams();
  const history = useHistory();
  const api = useApi();

  const [maintLogs, setMaintLogs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [rmas, setRmas] = useState([]);
  const [reports, setReports] = useState([]);
  const [installs, setInstalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  // Which serial has already finished a fetch. `loading` belongs to the FIRST
  // load of a serial and to nothing else: a poll tick that flipped it would
  // drop a drone with an empty history back into the full-page LoadingIcon
  // every ten seconds, unmounting the table and flashing the screen. Keyed by
  // serial rather than a plain boolean, so navigating to another drone counts
  // as a first load again.
  const loadedSerial = useRef(null);

  const shortSerial = serialnumber;

  // Format maintenance logs
  const arrangeMaint = (logs) =>
    logs.map((log) => ({
      id: `maint-${log.id}`,
      rawId: log.id,
      date: dayjs(log.dateofservice).format("MMM-DD-YY HH:mm"),
      sortDate: dayjs(log.dateofservice).valueOf(),
      desc: log.descript,
      drone_serial: log.drone_serial,
      user: log.firstname || 'Unknown',
      type: "maint",
    }));

  // Component genealogy (part_install_events). Structured, unlike the maintlist
  // prose assembly used to write, so no parsing is involved here.
  const arrangeInstalls = (events) =>
    events.map((e) => ({
      id: `install-${e.id}`,
      rawId: e.id,
      date: dayjs(e.occurred_at).format("MMM-DD-YY HH:mm"),
      sortDate: dayjs(e.occurred_at).valueOf(),
      desc: `${e.action === 'install' ? 'Fitted' : 'Removed'} ${e.component_type}`
        + ` ${e.board_uid || e.part_sku || ''}`.trimEnd()
        + (e.source === 'backfill' ? ' (reconstructed, date approximate)' : ''),
      drone_serial: serialnumber,
      user: e.operator_firstname || 'Unknown',
      type: "install",
    }));

  // Format flag logs
  const arrangeFlags = (flagsData) =>
    flagsData.map((log) => ({
      id: `flag-${log.id}`,
      rawId: log.id,
      date: dayjs(log.dateflagged).format("MMM-DD-YY HH:mm"),
      sortDate: dayjs(log.dateflagged).valueOf(),
      desc: log.descript,
      drone_serial: log.drone_serial,
      user: log.firstname || 'Unknown',
      type: "flag",
      active: log.active,
    }));

  // Format RMA logs
  const arrangeRmas = (rmasData) =>
    rmasData.map((rma) => {
      const itemStatus = rma.item_status || 'pending_review';
      const repairStatus = rma.repair_status;

      let displayStatus = itemStatus;
      if (itemStatus === 'approved' && repairStatus && repairStatus !== 'pending') {
        displayStatus = repairStatus;
      }

      const isActive = itemStatus === 'pending_review' ||
        (itemStatus === 'approved' && !['completed', 'shipped', 'unrepairable'].includes(repairStatus));

      const category = rma.issue_category
        ? rma.issue_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        : 'RMA';

      return {
        id: `rma-${rma.id}`,
        rawId: rma.id,
        date: dayjs(rma.created_at).format("MMM-DD-YY HH:mm"),
        sortDate: dayjs(rma.created_at).valueOf(),
        desc: rma.issue_description || category,
        category,
        drone_serial: serialnumber,
        user: rma.customer_name || rma.customer_email?.split('@')[0] || 'Customer',
        type: "rma",
        rma_number: rma.rma_number,
        status: displayStatus,
        itemStatus,
        isActive,
      };
    });

  // Format reports (from pilot app)
  const arrangeReports = (reportsData) =>
    reportsData.map((report) => ({
      id: `report-${report.id}`,
      rawId: report.id,
      date: dayjs(report.date_reported).format("MMM-DD-YY HH:mm"),
      sortDate: dayjs(report.date_reported).valueOf(),
      desc: report.issue_message,
      drone_serial: report.drone_serial,
      user: report.organization_name || report.server_id || 'Pilot',
      type: "report",
    }));

  // Fetch all logs
  const getLogs = async () => {
    if (loadedSerial.current !== serialnumber) setLoading(true);
    try {
      const [maintData, flagsData, rmasData, reportsData, installData] = await Promise.all([
        api.get(`/maintenance/${serialnumber}`, { silent: true }).catch(() => []),
        api.get(`/flags/all/${serialnumber}`, { silent: true }).catch(() => []),
        api.get(`/rma/drone/${serialnumber}`, { silent: true }).catch(() => []),
        api.get(`/reports/${serialnumber}`, { silent: true }).catch(() => []),
        api.get(`/scanner/installs/${serialnumber}`, { silent: true }).catch(() => ({ events: [] })),
      ]);

      setMaintLogs(arrangeMaint(maintData || []));
      setFlags(arrangeFlags(flagsData || []));
      setRmas(arrangeRmas(rmasData || []));
      setReports(arrangeReports(reportsData || []));
      setInstalls(arrangeInstalls(installData?.events || []));
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      // Marked whether or not the fetch worked: one attempt has landed, so the
      // screen has something to say (rows, or "No history found"), and no poll
      // tick may put the full-page loader back over it.
      loadedSerial.current = serialnumber;
      setLoading(false);
    }
  };

  useEffect(() => {
    getLogs();
    const interval = setInterval(getLogs, 10000);
    return () => clearInterval(interval);
  }, [serialnumber]);

  // Filter and combine logs. This memo IS the table's row order: an open RMA
  // rides at the top of the drone's history, and everything else falls in date
  // order under it.
  const listItems = useMemo(() => {
    let items = [];
    if (filterType === 'all') items = [...maintLogs, ...flags, ...rmas, ...reports, ...installs];
    else if (filterType === 'maint') items = maintLogs;
    else if (filterType === 'flags') items = flags;
    else if (filterType === 'rmas') items = rmas;
    else if (filterType === 'reports') items = reports;
    else if (filterType === 'installs') items = installs;

    // Sort: Active RMAs first, then by date descending. Copied first, because
    // every branch except 'all' hands back the state array itself, and sorting
    // that in place reorders state behind React's back: the next 'all' pass
    // then merges arrays somebody else already shuffled.
    return [...items].sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return b.sortDate - a.sortDate;
    });
  }, [maintLogs, flags, rmas, reports, installs, filterType]);

  // A first-paint gate. `loading` is true only while a serial's first fetch is
  // in flight (see getLogs), so poll refetches leave the table mounted and
  // nothing flashes. The row check stays: switching drones keeps the previous
  // drone's rows on screen until the new ones land, which beats a blank.
  if (loading && listItems.length === 0) return <LogoSpinner label="Loading the history" />;

  return (
    <PageLayout kind="workspace" className="pt-4">
      {/* Header */}
      <div className="mb-6">
        <BackLink to={`/drone/${serialnumber}`} label="Drone" className="mb-3" />
        <h1 className="text-2xl font-bold text-white mb-1">
          Vehicle History
          <span className="ml-3 text-primary">{shortSerial}</span>
        </h1>
        <p className="text-on-surface-variant text-sm">
          {listItems.length} total entries
        </p>
      </div>

      {/* Filter */}
      <SegTrack
        label="Filter history"
        wrap
        className="mb-4"
        value={filterType}
        onChange={setFilterType}
        options={[
          { key: 'all', label: 'All', count: maintLogs.length + flags.length + rmas.length + reports.length + installs.length },
          { key: 'maint', label: 'Maintenance', count: maintLogs.length },
          { key: 'flags', label: 'Flags', count: flags.length },
          { key: 'rmas', label: 'RMAs', count: rmas.length },
          { key: 'reports', label: 'Reports', count: reports.length },
          { key: 'installs', label: 'Build', count: installs.length },
        ]}
      />

      {/* The table. No box around it: the shared table renders its own vessel,
          so one here would draw a second one around the real one.

          No pagination any more. This is one drone's own history — every fetch
          is scoped to the serial, and the install feed is the only one with a
          server cap (200) — so the pile is tens of rows, not thousands. A
          `stickyHeader` vessel scrolls the lot with the header band held, which
          is strictly less work than paging through three pages of 25.

          No getRowId on purpose either. The obvious one, `item.id`, is not
          unique: /rma/drone/:serial joins rma_status_view to rma_items and
          selects the RMA's id, and nothing stops one RMA from carrying two
          items naming the same drone serial (rma_items has no unique on
          rma_id + serial_number), so two rows would arrive as `rma-<same>`.
          Index row ids are what this page has always keyed on, and nothing
          here depends on row identity: no in-cell editor, no keyboard
          selection, no animation ref. */}
      <DataTable
        data={listItems}
        columns={HISTORY_COLUMNS}
        stickyHeader
        rowProps={(item) => ({
          className: rowTint(item),
          style: rowStyle(item),
          // RMA rows only. The old handler was on every row and no-opped on
          // the other four types, which meant a pointer press anywhere in the
          // history ran a type check; now a non-RMA row carries no handler at
          // all.
          onClick:
            item.type === 'rma'
              ? () => history.push(`/rma/${item.rma_number}`)
              : undefined,
        })}
        empty={
          <EmptyState variant="area">No history found</EmptyState>
        }
      />
    </PageLayout>
  );
};

export default VehicleHistory;
